import negate from 'lodash.negate'
import { project, by } from '../../utils'

const COLOUR_ATTRIBUTE_TYPE_NAME = 'Colour'

const merger = (map, fields) => ({ ...map, ...fields })
const crosser = (total, set) => total.map(x => set.map(y => [...x, y])).reduce(by.aggregate.concat)

const mapperMerger = mapper => (map, fields) => merger(map, mapper(map)(fields))
const byMapMerging = mapper => [mapperMerger(mapper), {}]
const byProduct = [crosser, [[]]]

const toTypeMapper = map => attribute => {
  const { type, value } = attribute
  return {
    [type]: [...new Set([value, ...(map[type] || [])])]
  }
}

const byTypeMapMerging = byMapMerging(toTypeMapper)

/**
 * Get all available defining attributes
 * @method  getDefiningAttributes
 * @return  Array    List of all possible attributes
 */
const getDefiningAttributes = variants =>
  variants
    .map(project.attributes)
    .reduce(by.aggregate.concat, [])
    .reduce(...byTypeMapMerging)

const getDefiningAttributeTypes = definingAttributes => Object.keys(definingAttributes)

const getDefiningSets = variants => {
  const attributes = getDefiningAttributes(variants)
  const types = getDefiningAttributeTypes(attributes)

  return types.map(type => attributes[type].map(value => ({ type, value, shortValue: value })))
}

const getDefiningSetsProduct = sets => sets.reduce(...byProduct)

const identifyVariantsWithDifferentAttributesThanProvidedOne = (
  variants,
  currentVariatAttribute,
  selectedVariantAttributes
) => {
  const { type } = currentVariatAttribute
  // If the type is Validity (days), return all variants
  if (type === 'Validity (days)') return variants

  const otherAttributes = selectedVariantAttributes.filter(negate(by.attributeOf(currentVariatAttribute)))

  return variants.map(variant => {
    if (
      variant.attributes.every(attr => {
        if (attr.type === type) {
          return true
        }
        return otherAttributes.some(by.attributeOf(attr))
      })
    ) {
      return variant
    }
    return {
      ...variant,
      otherCombination: true
    }
  })
}

// Get all variants with same attributes except for one unique attribute
// Example: Get all variants of a SKU with the same color except for service type (the unique attribute)
const getSimilarVariants = (allVariants, uniqueAttribute, partNumber) => {
  const selectedVariant = allVariants?.find(variant => variant.partNumber === partNumber)
  const { attributes: selectedVariantAttributes = [] } = selectedVariant || {}
  const uniqueVariantType = selectedVariantAttributes?.find(attribute => attribute.type === uniqueAttribute)

  if (uniqueVariantType) {
    // Get all attributes of the selected variant excluding the unique attribute
    const nonUniqueAttributes = selectedVariantAttributes?.filter(attribute => attribute !== uniqueVariantType)

    const similarVariants = allVariants
      .filter(variant => {
        const { attributes } = variant
        // For each variant, check that all attributes match the attributes of the selected variant EXCEPT for the unique attribute
        const hasSimilarVariants = attributes
          .filter(attribute => attribute.type !== uniqueAttribute)
          .every(attribute => {
            return nonUniqueAttributes.some(otherAttribute => {
              return otherAttribute.type === attribute.type && otherAttribute.value === attribute.value
            })
          })
        return hasSimilarVariants
      })
      .map(variant => {
        return variant.partNumber
      })

    return similarVariants
  }
  return []
}

/**
 * Function to group by attributes
 * @method  groupBy
 * @return  Array    List of grouped variants with value as a key
 */
const groupByTypeValue = (data, type, key) => {
  return data.reduce((storage, item) => {
    const group =
      item.attributes.find(attr => attr.type === type) && item.attributes.find(attr => attr.type === type)[key] // defensive code because sometimes attr are missing
    if (group) {
      storage[group] = storage[group] || []
      storage[group].push(item)
    }
    return storage
  }, {})
}

// Given a partNumber, return a list of variant partNumbers with the same attributes except for the typeValue passed in
const getVariantsForType = (variantsGroupedByType, typeValue, partNumber) => {
  const variantTypes = Object.keys(variantsGroupedByType)
    .map(type => {
      if (typeValue === type) {
        return variantsGroupedByType[typeValue].find(combination => combination.partNumber === partNumber)
      }

      const variantsWithSameAttributes = variantsGroupedByType[type].filter(
        combination => !combination.otherCombination
      )

      if (variantsWithSameAttributes.length) {
        return variantsWithSameAttributes[0]
      }

      // If variant for this type doesn't exist, return null and filter them out afterwards
      return null
    })
    .filter(variant => variant)
  return variantTypes
}

const sortSizeVariantsBySizeAsc = variants => {
  return variants?.sort((firstElement, secondElement) => {
    const firstElementOrder = firstElement.attributes?.find(attribute => attribute.type === 'Size')?.order
    const secondElementOrder = secondElement.attributes?.find(attribute => attribute.type === 'Size')?.order
    return firstElementOrder - secondElementOrder
  })
}

// Return all variants that have the same matching Colour attribute of a SKU
const getMatchingColourVariants = (variants, partNumber) => {
  const partNumberVariant = variants?.find(variant => variant.partNumber === partNumber)
  const colourVariantValue = partNumberVariant?.attributes?.find(attr => attr.type === COLOUR_ATTRIBUTE_TYPE_NAME)
    ?.value

  // If product has no colour attribute, return all variants
  if (!colourVariantValue) {
    return variants
  }

  // Return only variants with the same matching colour attribute
  return variants?.filter(variant =>
    variant?.attributes?.some(attribute => {
      return attribute.type === COLOUR_ATTRIBUTE_TYPE_NAME && attribute.value === colourVariantValue
    })
  )
}

export {
  getDefiningAttributes,
  getDefiningAttributeTypes,
  getDefiningSets,
  getDefiningSetsProduct,
  getMatchingColourVariants,
  getSimilarVariants,
  getVariantsForType,
  toTypeMapper,
  identifyVariantsWithDifferentAttributesThanProvidedOne,
  groupByTypeValue,
  sortSizeVariantsBySizeAsc
}
